import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import CustomMarkdown from "@/components/content/custome-markdown";
import { Button } from "@/components/ui/button";
import { FileText, FileText as FileTextIcon, Sparkles, Brain } from 'lucide-react';
import { SummaryFloatingActionButtons } from './SummaryFloatingActionButtons';
import { useIsMobile } from "@/hooks/use-mobile";
import { EmptyState } from '../ui/interactive-empty-state';
import { SpinnerIcon } from '../ui/SpinnerIcon';
import { useI18n } from '@/lib/i18n';

interface SummarySectionProps {
  summary: string;
  isSummaryLoading: boolean;
  onGenerateSummary: () => void | Promise<void>;
  player?: any;
  episodeId?: string;
  onTranslationStateChange?: (showTranslation: boolean) => void;
}

export const SummarySection = React.memo(
  ({ summary, isSummaryLoading, onGenerateSummary, player, episodeId, onTranslationStateChange }: SummarySectionProps) => {
    const { t } = useI18n();
    const contentRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();

    // Translation state
    const [showTranslation, setShowTranslation] = useState(false);
    const [translatedSummary, setTranslatedSummary] = useState<string>('');
    const [isGeneratingTranslation, setIsGeneratingTranslation] = useState(false);
    
    // Summary generation state
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

    // Handle translation toggle
    const handleToggleTranslation = useCallback(async () => {
      if (!episodeId) {
        console.error('Episode ID is required for translation');
        return;
      }

      if (showTranslation) {
        setShowTranslation(false);
        return;
      }

      // If we already have the translation, just show it
      if (translatedSummary) {
        setShowTranslation(true);
        return;
      }

      // Generate translation
      setIsGeneratingTranslation(true);
      try {
        const response = await fetch('/api/translate-summary-gemini', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ episodeId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate translation');
        }

        const result = await response.json();
        setTranslatedSummary(result.translation);
        setShowTranslation(true);
      } catch (error) {
        console.error('Error generating translation:', error);
        alert(`Failed to generate translation: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsGeneratingTranslation(false);
      }
    }, [episodeId, showTranslation, translatedSummary]);

    // Handle summary generation
    const handleGenerateSummary = useCallback(() => {
      setIsGeneratingSummary(true);
      // Call onGenerateSummary and handle the promise if it returns one
      const result = onGenerateSummary();
      if (result && typeof result.then === 'function') {
        result.finally(() => {
          setIsGeneratingSummary(false);
        });
      } else {
        // If it's not a promise, reset the state after a short delay
        setTimeout(() => {
          setIsGeneratingSummary(false);
        }, 100);
      }
    }, [onGenerateSummary]);

    // Notify parent about translation state changes
    useEffect(() => {
      if (onTranslationStateChange) {
        onTranslationStateChange(showTranslation);
      }
    }, [showTranslation, onTranslationStateChange]);

    // Split markdown content into blocks for mobile translation display
    const splitMarkdownIntoBlocks = useCallback((markdown: string) => {
      if (!markdown) return [];
      
      // Normalize line endings and remove excessive whitespace
      const normalizedMarkdown = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      
      // Split by major structural elements
      let blocks = [];
      
      // First, split by headers (# ## ### etc.)
      const headerSplit = normalizedMarkdown.split(/\n(?=#{1,6}\s)/);
      
      for (const section of headerSplit) {
        const trimmedSection = section.trim();
        if (!trimmedSection) continue;
        
        // Check if this section starts with a header
        if (/^#{1,6}\s/.test(trimmedSection)) {
          // Split header sections by double newlines (paragraphs)
          const paragraphs = trimmedSection.split(/\n\s*\n/);
          blocks.push(...paragraphs.map(p => p.trim()).filter(p => p.length > 0));
        } else {
          // For non-header sections, split by double newlines
          const paragraphs = trimmedSection.split(/\n\s*\n/);
          blocks.push(...paragraphs.map(p => p.trim()).filter(p => p.length > 0));
        }
      }
      
      // If no clear structure found, split by double newlines as fallback
      if (blocks.length <= 1) {
        blocks = normalizedMarkdown
          .split(/\n\s*\n/)
          .map(block => block.trim())
          .filter(block => block.length > 0);
      }
      
      // Additional fallback: if still too few blocks, split by single newlines for very short content
      if (blocks.length <= 1 && normalizedMarkdown.length > 200) {
        blocks = normalizedMarkdown
          .split(/\n/)
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .reduce((acc: string[], line: string, index: number) => {
            // Group every 2-3 lines together
            const groupIndex = Math.floor(index / 3);
            if (!acc[groupIndex]) acc[groupIndex] = '';
            acc[groupIndex] += (acc[groupIndex] ? '\n' : '') + line;
            return acc;
          }, []);
      }
      
      return blocks;
    }, []);

    // Memoized content blocks for both original and translated content
    const originalBlocks = useMemo(() => splitMarkdownIntoBlocks(summary), [summary, splitMarkdownIntoBlocks]);
    const translatedBlocks = useMemo(() => splitMarkdownIntoBlocks(translatedSummary), [translatedSummary, splitMarkdownIntoBlocks]);

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }, [summary]);

    if (!summary) {
      return (
        <div className="flex flex-col w-full bg-background/30 backdrop-blur-sm rounded-lg border border-border/50">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-primary rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">{t('summary.title')}</h3>
            </div>
          </div>
          <EmptyState
              title={isGeneratingSummary ? t('summary.generating') : t('summary.noSummary')}
              description={isGeneratingSummary ? t('summary.generatingDesc') : t('summary.generateDesc')}
              icons={[<FileTextIcon key="p1" className="h-6 w-6" />, <Brain key="p2" className="h-6 w-6" />, <Sparkles key="p3" className="h-6 w-6" />]}
              action={{
                label: isGeneratingSummary ? t('summary.generating') : t('summary.generate'),
                icon: isGeneratingSummary ? <SpinnerIcon size="sm" /> : <FileTextIcon className="h-4 w-4" />,
                onClick: handleGenerateSummary,
                disabled: isGeneratingSummary
              }}
            />
        </div>
      );
    }

    return (
      <>
        {/* Floating Action Buttons */}
        {episodeId && (
          <SummaryFloatingActionButtons
            showTranslation={showTranslation}
            onToggleTranslation={handleToggleTranslation}
            isGeneratingTranslation={isGeneratingTranslation}
          />
        )}

        <div className="mt-2 bg-background/30 backdrop-blur-sm rounded-lg border border-border/50">
          {/* Header similar to TranscriptSection */}
          <div className="flex items-center justify-between p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-primary rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">
                {t('summary.title')}{showTranslation ? ` - ${t('summary.zhTranslation')}` : ''}
              </h3>
            </div>
            <div className="text-sm text-muted-foreground">{t('summary.aiOverview')}</div>
          </div>
          
          <div className="flex-1 overflow-y-auto pb-8 px-6">
            {isMobile ? (
              // Mobile: Show translation blocks inline below corresponding original blocks
              <div
                ref={contentRef}
                className="max-w-4xl mx-auto transition-all duration-300"
              >
                {showTranslation && translatedSummary ? (
                  // Block-by-block display when translation is active
                  <div className="space-y-6">
                    {originalBlocks.map((originalBlock, index) => (
                      <div key={index} className="space-y-3">
                        {/* Original block */}
                        <div className="prose prose-lg dark:prose-invert max-w-none">
                          <CustomMarkdown
                            markdown={originalBlock}
                            className="leading-relaxed"
                            fontSize={18}
                            lineHeight={1.7}
                            player={player}
                            showTimeButtons={true}
                          />
                        </div>
                        
                        {/* Corresponding translation block */}
                        {translatedBlocks[index] ? (
                          <div className="ml-2 pl-4 border-l-3 border-primary/40 bg-gradient-to-r from-primary/5 via-primary/3 to-transparent rounded-r-xl py-3 pr-4 mt-2 transform transition-all duration-300 ease-out animate-in slide-in-from-left-2 fade-in">
                            <div className="flex items-start gap-2 mb-2">
                              <div className="text-xs font-medium text-primary/70 bg-primary/10 px-2 py-1 rounded-md flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a18.694 18.694 0 01-3.107 3.78 1 1 0 01-1.334-1.49 16.729 16.729 0 002.778-3.102A18.94 18.94 0 013.3 8H2a1 1 0 110-2h2.73c.11-.67.27-1.331.48-1.985A1 1 0 017 2z" clipRule="evenodd" />
                                </svg>
                                {t('summary.zhTranslation')}
                              </div>
                            </div>
                            <div className="prose prose-lg dark:prose-invert max-w-none">
                              <CustomMarkdown
                                markdown={translatedBlocks[index]}
                                className="leading-relaxed text-muted-foreground/90"
                                fontSize={16}
                                lineHeight={1.6}
                                player={player}
                                showTimeButtons={false}
                              />
                            </div>
                          </div>
                        ) : (
                          // If no matching translation block exists, show a fallback
                          index < translatedBlocks.length && (
                            <div className="ml-2 pl-4 border-l-3 border-muted/40 bg-muted/10 rounded-r-xl py-3 pr-4 mt-2">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="text-xs font-medium text-muted-foreground/70 bg-muted/20 px-2 py-1 rounded-md">
                                  {t('summary.translationLoading')}
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ))}
                    
                    {/* Show any remaining translation blocks if there are more translations than original blocks */}
                    {translatedBlocks.length > originalBlocks.length && (
                      <div className="space-y-4 mt-6">
                        {translatedBlocks.slice(originalBlocks.length).map((translatedBlock, index) => (
                          <div key={`extra-${index}`} className="ml-2 pl-4 border-l-3 border-primary/40 bg-gradient-to-r from-primary/5 via-primary/3 to-transparent rounded-r-xl py-3 pr-4 transform transition-all duration-300 ease-out">
                            <div className="flex items-start gap-2 mb-2">
                              <div className="text-xs font-medium text-primary/70 bg-primary/10 px-2 py-1 rounded-md flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a18.694 18.694 0 01-3.107 3.78 1 1 0 01-1.334-1.49 16.729 16.729 0 002.778-3.102A18.94 18.94 0 013.3 8H2a1 1 0 110-2h2.73c.11-.67.27-1.331.48-1.985A1 1 0 017 2z" clipRule="evenodd" />
                                </svg>
                                {t('summary.zhTranslationExtra')}
                              </div>
                            </div>
                            <div className="prose prose-lg dark:prose-invert max-w-none">
                              <CustomMarkdown
                                markdown={translatedBlock}
                                className="leading-relaxed text-muted-foreground/90"
                                fontSize={16}
                                lineHeight={1.6}
                                player={player}
                                showTimeButtons={false}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Normal display when translation is off
                  <div className="prose prose-lg dark:prose-invert max-w-none">
                    <CustomMarkdown
                      markdown={summary}
                      className="leading-relaxed"
                      fontSize={18}
                      lineHeight={1.7}
                      player={player}
                      showTimeButtons={true}
                    />
                  </div>
                )}
              </div>
            ) : (
              // Desktop: Show side-by-side with 3:2 ratio
              <div className="flex max-w-7xl mx-auto transition-all duration-700 ease-out">
                {/* Original Content - Left Side (3/5) */}
                <div className={`transition-all duration-700 ease-out ${
                  showTranslation ? 'w-3/5 px-4' : 'w-full'
                }`}>
                  <div
                    ref={contentRef}
                    className="transition-all duration-300"
                  >
                    <div className="prose prose-lg dark:prose-invert max-w-none">
                      <CustomMarkdown
                        markdown={summary}
                        className="leading-relaxed"
                        fontSize={18}
                        lineHeight={1.7}
                        player={player}
                        showTimeButtons={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Vertical Separator */}
                <div className={`w-px bg-border/60 self-stretch transition-all duration-700 ease-out transform ${
                  showTranslation 
                    ? 'translate-x-0 opacity-100' 
                    : '-translate-x-[400px] opacity-0'
                }`}></div>

                {/* Translation Content - Right Side (2/5) */}
                <div className={`transition-all duration-700 ease-out transform ${
                  showTranslation 
                    ? 'w-2/5 translate-x-0 opacity-100 px-4' 
                    : 'w-0 -translate-x-[400px] opacity-0 overflow-hidden'
                }`}>
                  {showTranslation && translatedSummary && (
                    <div className="transition-all duration-300">
                      <div className="prose prose-lg dark:prose-invert max-w-none">
                        <CustomMarkdown
                          markdown={translatedSummary}
                          className="leading-relaxed text-muted-foreground"
                          fontSize={18}
                          lineHeight={1.7}
                          player={player}
                          showTimeButtons={true}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
);

SummarySection.displayName = "SummarySection";
